// src/components/RoleBasedRedirect.tsx
/**
 * RoleBasedRedirect
 * Компонент для автоматического перенаправления пользователя на нужный интерфейс в зависимости от статуса авторизации и роли.
 *
 * Sequence diagram (Mermaid):
 *
 * ```mermaid
 * sequenceDiagram
 *   participant UI as Frontend UI
 *   participant Store as AuthStore
 *   participant Redirect as RoleBasedRedirect
 *
 *   UI->>Store: useAuthStore()
 *   Store-->>Redirect: { isAuth, role }
 *   alt Не авторизован
 *     Redirect-->>UI: <Navigate to="/login" />
 *   else Авторизован
 *     Redirect-->>UI: <Navigate to="/recruiter/reports" />
 *   end
 *
 *   Note over Redirect,UI: В будущем можно добавить логику по ролям (admin, candidate, viewer)
 * ```
 *
 * Бизнес-логика:
 * - Если пользователь не авторизован — редирект на /login
 * - Если авторизован — редирект на /recruiter/reports (по умолчанию)
 * - В будущем: можно добавить ролевую логику (admin → /admin, candidate → /interview/:id и т.д.)
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { stripHhOAuthQuery } from '../relevanter/services/hhOAuthCallback';

const RoleBasedRedirect: React.FC = () => {
  const { isAuth } = useAuthStore();
  const location = useLocation();
  const safeSearch = stripHhOAuthQuery(location.search);

  if (!isAuth) {
    return <Navigate to={`/login${safeSearch}`} replace />;
  }

  // По умолчанию все попадают на страницу отчетов в рекрутерском интерфейсе
  return <Navigate to={`/recruiter/reports${safeSearch}`} replace />;
};

export default RoleBasedRedirect;
